// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the chevron menu on an action chip (H6, J4, J5): "Konvo can", then
// every action the one drafted request allows (Task for {person}, Task for
// you, Remind me, Create event with {people} only when a meeting was proposed,
// Block time to do it, Nothing to do here). When the user's habits picked the
// action, a "why" line leads. Picking an item switches the chip; it does not
// act, so the user still presses + (or "Edit before creating" for the form).

import {ChipAvatar} from '@app/features/konvo_assistant/components/action_chip/ActionChipAvatar';
import styles from '@app/features/konvo_assistant/components/action_chip/ActionChipMenu.module.css';
import {
	chipLabel,
	menuLabel,
	menuMeta,
	T,
	whyText,
} from '@app/features/konvo_assistant/components/action_chip/ActionChipText';
import type {ActionChipController} from '@app/features/konvo_assistant/components/action_chip/state/ActionChipRun';
import type {ChoiceKey} from '@app/features/konvo_assistant/state/AssistantSuggestions';
import {CheckIcon, PencilSimpleIcon, SparkleIcon} from '@app/features/ui/icons/Icons';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {Fragment, useEffect, useRef} from 'react';

interface ActionChipMenuProps {
	chip: ActionChipController;
	onPick: (key: ChoiceKey) => void;
	/** "Edit before creating": the full prefilled form for the current action. */
	onEdit: () => void;
	onClose: () => void;
}

export const ActionChipMenu = observer(({chip, onPick, onEdit, onClose}: ActionChipMenuProps) => {
	const {i18n} = useLingui();
	const listRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		listRef.current?.querySelector<HTMLButtonElement>('[aria-checked="true"], button')?.focus();
	}, []);
	const why = chip.why;
	const onKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Escape') {
			event.preventDefault();
			onClose();
			return;
		}
		if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
		event.preventDefault();
		const items = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('button[role^="menuitem"]') ?? [])];
		const at = items.indexOf(document.activeElement as HTMLButtonElement);
		const next = items[(at + (event.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length];
		next?.focus();
	};
	const edit = (
		<>
			<div className={styles.separator} aria-hidden="true" />
			<button
				type="button"
				role="menuitem"
				className={styles.item}
				onClick={onEdit}
				data-flx="konvo-assistant.action-chip.menu.edit"
			>
				<span className={styles.itemIcon} aria-hidden="true">
					<PencilSimpleIcon size={16} />
				</span>
				<span className={styles.itemText}>
					<span className={styles.itemTitle}>{i18n._(T.openForm)}</span>
				</span>
			</button>
		</>
	);
	return (
		<div
			className={styles.menu}
			role="menu"
			aria-label={i18n._(T.konvoCan)}
			onKeyDown={onKeyDown}
			ref={listRef}
			data-flx="konvo-assistant.action-chip.menu"
		>
			{why && (
				<div className={styles.why} data-flx="konvo-assistant.action-chip.why">
					<SparkleIcon size={15} className={styles.whyIcon} aria-hidden="true" />
					<span>
						<strong>{chipLabel(i18n, chip, why.chosen)}</strong> {whyText(i18n, why)}
					</span>
				</div>
			)}
			<div className={styles.heading}>{i18n._(T.konvoCan)}</div>
			{chip.choices.map((key) => {
				const current = key === chip.choice;
				const meta = menuMeta(i18n, chip, key);
				return (
					<Fragment key={key}>
						{key === 'none' && edit}
						{key === 'none' && <div className={styles.separator} aria-hidden="true" />}
						<button
							type="button"
							role="menuitemradio"
							aria-checked={current}
							className={clsx(styles.item, current && styles.itemCurrent, key === 'none' && styles.itemQuiet)}
							onClick={() => onPick(key)}
							data-flx={`konvo-assistant.action-chip.menu.${key}`}
						>
							<ChipAvatar chip={chip} choice={key} size="menu" />
							<span className={styles.itemText}>
								<span className={styles.itemTitle}>{menuLabel(i18n, chip, key)}</span>
								{meta && <span className={styles.itemMeta}>{meta}</span>}
							</span>
							{current && <CheckIcon size={16} weight="bold" className={styles.check} aria-hidden="true" />}
						</button>
					</Fragment>
				);
			})}
			{!chip.choices.includes('none') && edit}
		</div>
	);
});
